import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import tradeFromScratchStore from "@/store/tradeFromScratchStore";
import { sendBondOptionTicket } from "@/store/tradeFromScratchStoreACTIONS";
import BondOptionCoreFields from "./bondOption/BondOptionCoreFields";
import BondOptionDetailFields from "./bondOption/BondOptionDetailFields";
import { useBondOptionTicket } from "./bondOption/useBondOptionTicket";
import {
  returnHighlightedKeys,
  useTicketShell,
} from "./helper/useTicketShell";
import TicketStatusLine from "./ticketFields/TicketStatusLine";
import TicketHeader from "./ticketFields/TicketHeader";

// LQC-2676 - the Leg Creation bond option ticket. The underlying comes from the same
// bondSecuritiesStore as the bond ticket; payment currency defaults to it but stays
// editable, and the calendar follows it.

interface IBondOptionTicketProps {
  ticketId: string;
}

const BondOptionTicket = ({ ticketId }: IBondOptionTicketProps) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const shell = useTicketShell(ticketId);
  const { missingEntries, dateIssue } = useBondOptionTicket(ticketId);
  const ensureTicket = tradeFromScratchStore(
    (state) => state.ensureBondOptionTicket,
  );
  const resetTicket = tradeFromScratchStore(
    (state) => state.resetBondOptionTicket,
  );

  useEffect(() => {
    ensureTicket(ticketId);
  }, [ticketId, ensureTicket]);

  const canSend =
    missingEntries.length === 0 && !dateIssue && !shell.isSending;
  const highlightedKeys = returnHighlightedKeys(
    shell.isHoveringSend,
    canSend,
    missingEntries,
  );

  const handleSend = async () => {
    const sent = await sendBondOptionTicket({
      ticketId,
      user,
      getAccessTokenSilently,
    });
    if (sent) shell.markSent();
  };

  return (
    <div
      data-testid="BondOptionTicketContainer"
      id="BondOptionTicketContainer"
      className="flex h-full w-full flex-col gap-2"
    >
      <TicketHeader
        testIdPrefix="BondOptionTicket"
        instrument={shell.instrument}
        onInstrumentChange={shell.setInstrument}
        canSend={canSend}
        isSending={shell.isSending}
        isConfirming={shell.isConfirmingSend}
        onSend={handleSend}
        onClear={() => resetTicket(ticketId)}
        onSendHoverChange={shell.setIsHoveringSend}
      />
      <TicketStatusLine
        testIdPrefix="BondOptionTicket"
        lastSent={shell.lastSent}
        missingFields={missingEntries.map((entry) => entry.label)}
        hasDateIssue={!!dateIssue}
        isBlocking={shell.isHoveringSend && !canSend}
      />
      <BondOptionCoreFields
        ticketId={ticketId}
        highlightedKeys={highlightedKeys}
      />
      <BondOptionDetailFields
        ticketId={ticketId}
        highlightedKeys={highlightedKeys}
      />
    </div>
  );
};

export default BondOptionTicket;
